import {FirmUserRoleStatus} from '@sollapay/enums';
import {MoreMenu} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import type {MoreMenuProps} from '@sollapay/ui/components';
import type {FC} from 'react';

export type TeamMemberMenuAction = 'remove';

/**
 * Every non-revoked member (active or pending) can be removed — status only changes what
 * removal *means* (revoke a pending invite vs. remove an active member), not whether the
 * action is offered. Kept as a function, mirroring {@link getTrustAccountMenuActions}, so a
 * future status (e.g. a phase-2 "suspended") can restrict it without touching call sites.
 */
export const getTeamMemberMenuActions = (_status: FirmUserRoleStatus): TeamMemberMenuAction[] => [
  'remove'
];

export interface TeamMemberMoreMenuProps {
  actions: TeamMemberMenuAction[];
  status: FirmUserRoleStatus;
  /** Omit to hide — the permission gate lives in the caller passing `undefined`. */
  onRemove?: () => void;
  orientation?: MoreMenuProps['orientation'];
  variant?: MoreMenuProps['variant'];
}

export const TeamMemberMoreMenu: FC<TeamMemberMoreMenuProps> = ({
  actions,
  status,
  onRemove,
  orientation,
  variant
}) => {
  const {t} = useTranslation('team');
  const isPending = status === FirmUserRoleStatus.PENDING;

  const actionConfig: Record<TeamMemberMenuAction, MoreMenuProps['items'][number]> = {
    remove: {
      icon: 'delete',
      label: t(isPending ? 'tableActions.cancelInvitation' : 'tableActions.removeMember'),
      onClick: () => onRemove?.(),
      destructive: true
    }
  };

  const handlerMap: Record<TeamMemberMenuAction, (() => void) | undefined> = {remove: onRemove};

  const items = actions.filter(action => !!handlerMap[action]).map(action => actionConfig[action]);

  return (
    <MoreMenu
      ariaLabel={t('tableActions.openMenu')}
      orientation={orientation}
      variant={variant}
      items={items}
    />
  );
};
